"use client";

import { recordNetwork } from "@/utils/diagnostics-buffer";

type DiagnosticSeverity = "info" | "warn" | "error" | "fatal";

type DiagnosticEvent = {
  eventType: string;
  severity?: DiagnosticSeverity;
  source?: string;
  route?: string | null;
  pageUrl?: string | null;
  apiPath?: string | null;
  apiMethod?: string | null;
  apiStatus?: number | null;
  durationMs?: number | null;
  traceId?: string | null;
  sessionId?: string | null;
  message?: string | null;
  stack?: string | null;
  componentStack?: string | null;
  details?: Record<string, unknown>;
  /** Skip sendBeacon (small size limit) and force a fetch POST — for large bundles. */
  forceFetch?: boolean;
};

declare global {
  interface Window {
    __TM_DIAGNOSTICS_INSTALLED__?: boolean;
    __TM_ORIGINAL_FETCH__?: typeof fetch;
    __TM_TRACE_ID__?: string;
    __TM_DIAGNOSTIC_SESSION_ID__?: string;
    __TM_DIAGNOSTIC_COUNTS__?: { minute: number; count: number };
  }
}

const DIAGNOSTICS_ENDPOINT = "/api/diagnostics/client-event";
const MAX_EVENTS_PER_MINUTE = 80;

function randomId(prefix: string) {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(16).slice(2) + Date.now().toString(16);
  return `${prefix}_${id}`;
}

export function getDiagnosticSessionId() {
  if (typeof window === "undefined") return null;
  if (window.__TM_DIAGNOSTIC_SESSION_ID__) return window.__TM_DIAGNOSTIC_SESSION_ID__;

  const key = "tm_diagnostic_session_id";
  const existing = window.sessionStorage.getItem(key);
  const next = existing || randomId("session");
  window.sessionStorage.setItem(key, next);
  window.__TM_DIAGNOSTIC_SESSION_ID__ = next;
  return next;
}

export function getDiagnosticTraceId() {
  if (typeof window === "undefined") return null;
  if (window.__TM_TRACE_ID__) return window.__TM_TRACE_ID__;
  const next = randomId("trace");
  window.__TM_TRACE_ID__ = next;
  return next;
}

function cleanUrl(value: string | null | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    for (const key of [...url.searchParams.keys()]) {
      if (/token|secret|code|state|password|key|auth/i.test(key)) {
        url.searchParams.set(key, "[redacted]");
      }
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return value.replace(/([?&](?:token|secret|code|state|password|key|auth)=)[^&]+/gi, "$1[redacted]");
  }
}

function route() {
  if (typeof window === "undefined") return null;
  return cleanUrl(`${window.location.pathname}${window.location.search}`);
}

function pageUrl() {
  if (typeof window === "undefined") return null;
  return cleanUrl(window.location.href);
}

function shouldThrottle() {
  if (typeof window === "undefined") return true;
  const minute = Math.floor(Date.now() / 60000);
  const counts = window.__TM_DIAGNOSTIC_COUNTS__;
  if (!counts || counts.minute !== minute) {
    window.__TM_DIAGNOSTIC_COUNTS__ = { minute, count: 1 };
    return false;
  }
  counts.count += 1;
  return counts.count > MAX_EVENTS_PER_MINUTE;
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }
  if (typeof error === "string") {
    return { message: error };
  }
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

export function reportClientDiagnostic(event: DiagnosticEvent) {
  if (typeof window === "undefined") return;
  // Manual captures are deliberate and rare — never throttle them away.
  if (!event.forceFetch && shouldThrottle()) return;

  const { forceFetch, ...rest } = event;
  const payload: DiagnosticEvent = {
    severity: "error",
    source: "browser",
    route: route(),
    pageUrl: pageUrl(),
    traceId: getDiagnosticTraceId(),
    sessionId: getDiagnosticSessionId(),
    ...rest,
  } as DiagnosticEvent & { source: string };

  const body = JSON.stringify(payload);

  // Large bundles (manual captures) exceed the sendBeacon size limit and would
  // be silently dropped — those force a real fetch POST instead.
  if (!forceFetch) {
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(DIAGNOSTICS_ENDPOINT, blob)) return;
      }
    } catch {
      // Fall through to fetch.
    }
  }

  const originalFetch = window.__TM_ORIGINAL_FETCH__ || window.fetch.bind(window);
  void originalFetch(DIAGNOSTICS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "include",
    keepalive: true,
  }).catch(() => undefined);
}

function requestInfo(input: RequestInfo | URL) {
  const request = input instanceof Request ? input : null;
  const rawUrl = request ? request.url : String(input);
  const url = new URL(rawUrl, window.location.origin);
  return {
    request,
    url,
    isSameOrigin: url.origin === window.location.origin,
    isApi: url.origin === window.location.origin && url.pathname.startsWith("/api/"),
    isDiagnostics: url.pathname.startsWith("/api/diagnostics/"),
  };
}

function withTraceHeaders(input: RequestInfo | URL, init?: RequestInit) {
  const { request, isApi, isSameOrigin, isDiagnostics } = requestInfo(input);
  if (!isApi || !isSameOrigin || isDiagnostics) return { input, init };

  const traceId = getDiagnosticTraceId();
  const sessionId = getDiagnosticSessionId();

  if (request) {
    const headers = new Headers(init?.headers || request.headers);
    if (traceId) headers.set("x-maestro-trace-id", traceId);
    if (sessionId) headers.set("x-maestro-session-id", sessionId);
    return {
      input: new Request(request, { ...init, headers }),
      init: undefined,
    };
  }

  const headers = new Headers(init?.headers);
  if (traceId) headers.set("x-maestro-trace-id", traceId);
  if (sessionId) headers.set("x-maestro-session-id", sessionId);
  return {
    input,
    init: { ...init, headers },
  };
}

export function installFetchDiagnostics() {
  if (typeof window === "undefined") return;
  if (window.__TM_ORIGINAL_FETCH__) return;

  const originalFetch = window.fetch.bind(window);
  window.__TM_ORIGINAL_FETCH__ = originalFetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const info = requestInfo(input);
    const startedAt = performance.now();
    const traced = withTraceHeaders(input, init);

    const method = init?.method || (input instanceof Request ? input.method : "GET");
    try {
      const response = await originalFetch(traced.input, traced.init);
      // Record EVERY same-origin/API call (not just failures) into the wide
      // buffer so slow-but-successful requests are visible in a capture.
      if (!info.isDiagnostics && (info.isApi || info.isSameOrigin)) {
        recordNetwork({
          method,
          url: cleanUrl(info.url.href) || info.url.pathname,
          status: response.status,
          ok: response.ok,
          durationMs: Math.round(performance.now() - startedAt),
          kind: info.isApi ? "api" : "same-origin",
        });
      }
      if (info.isApi && !info.isDiagnostics && !response.ok) {
        const clone = response.clone();
        void clone.text().then((text) => {
          reportClientDiagnostic({
            eventType: "api_error",
            severity: response.status >= 500 ? "error" : "warn",
            apiPath: cleanUrl(info.url.href),
            apiMethod: init?.method || (input instanceof Request ? input.method : "GET"),
            apiStatus: response.status,
            durationMs: performance.now() - startedAt,
            message: `API ${response.status} ${info.url.pathname}`,
            details: {
              statusText: response.statusText,
              responseSnippet: text.slice(0, 700),
            },
          });
        }).catch(() => undefined);
      }
      return response;
    } catch (error) {
      if (!info.isDiagnostics && (info.isApi || info.isSameOrigin)) {
        recordNetwork({
          method,
          url: cleanUrl(info.url.href) || info.url.pathname,
          status: null,
          ok: false,
          durationMs: Math.round(performance.now() - startedAt),
          kind: info.isApi ? "api" : "same-origin",
        });
      }
      if (info.isApi && !info.isDiagnostics) {
        const normalized = normalizeError(error);
        reportClientDiagnostic({
          eventType: "api_network_error",
          severity: "error",
          apiPath: cleanUrl(info.url.href),
          apiMethod: method,
          durationMs: performance.now() - startedAt,
          message: normalized.message,
          stack: normalized.stack,
          details: { errorName: normalized.name },
        });
      }
      throw error;
    }
  };
}

export function reportUnhandledError(error: unknown, eventType: string, extra?: Record<string, unknown>) {
  const normalized = normalizeError(error);
  reportClientDiagnostic({
    eventType,
    severity: "error",
    message: normalized.message,
    stack: normalized.stack,
    details: {
      errorName: normalized.name,
      ...extra,
    },
  });
}
